import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, ExternalLink, Plus, Pencil, Trash2, MessageCircle, ListChecks, Search, Check } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import AskCassieEditor from "./AskCassieEditor";
import AskCassieImport from "./AskCassieImport";
import AskCassieTransfer from "./AskCassieTransfer";
import AskCassieReviewQueue from "./AskCassieReviewQueue";
import AskCassieVideoIntro from "./AskCassieVideoIntro";
import AskCassieAudit from "./AskCassieAudit";
import AskCassieNewAnswers from "./AskCassieNewAnswers";
import AskCassieStats from "./AskCassieStats";

const Card = ({ title, subtitle, children, action }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display font-bold text-navy">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const TABS = [
  { key: "answers", label: "Answers" },
  { key: "holding", label: "Needs a subject" },
  { key: "reviews", label: "Reviews due" },
  { key: "csv", label: "Import & export" },
  { key: "gaps", label: "What we can't answer" },
  { key: "stats", label: "Visitors & questions" },
  { key: "video", label: "Video intro" },
  { key: "audit", label: "Content audit" },
  { key: "batch", label: "New answers" },
  { key: "settings", label: "Reviewer & launch" },
];

const input = "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy focus:outline-none focus:border-orange";
const PAGE = 50;

export default function AskCassieManager() {
  const { toast } = useToast();
  const [tab, setTab] = useState("answers");
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [editing, setEditing] = useState(null); // {} for new, answer for edit
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewer, setReviewer] = useState(null);
  // Working through 300 imported drafts: filter, select, act on the selection.
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("draft");
  const [review, setReview] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [sel, setSel] = useState([]);
  const [queue, setQueue] = useState(null);
  const [moveTo, setMoveTo] = useState("");

  const load = async () => {
    const [a, o] = await Promise.all([adminApi.askCassie.answers(), adminApi.askCassie.options()]);
    setData(a);
    setOptions(o);
    setReviewer(o.config?.reviewer_stored || {});
  };
  useEffect(() => { load().catch(() => setData({ answers: [] })); }, []);
  useEffect(() => {
    if (tab === "gaps") adminApi.askCassie.gaps().then(setGaps).catch(() => setGaps({ themes: [], theme_count: 0, asked_count: 0, ignored_count: 0 }));
    if (tab === "reviews") adminApi.askCassie.reviewReport().then(setReviews).catch(() => setReviews({ due: [], overdue: [] }));
  }, [tab]);

  const removeGap = async (id) => {
    await adminApi.askCassie.removeUnanswered(id).catch(() => {});
    setGaps(await adminApi.askCassie.gaps().catch(() => gaps));
  };

  const resolveGap = async (t, action) => {
    const ids = (t.asked || []).map((a) => a.id).filter(Boolean);
    if (!ids.length) return;
    if (action === "spam" && !window.confirm(`Delete "${t.label}" and the ${ids.length} question${ids.length === 1 ? "" : "s"} behind it?`)) return;
    const res = await adminApi.askCassie.resolveGaps(ids, action).catch(() => null);
    if (res) toast({ title: action === "spam" ? "Deleted as spam" : "Marked as answered" });
    setGaps(await adminApi.askCassie.gaps().catch(() => gaps));
  };

  const unspamGap = async (id) => {
    await adminApi.askCassie.unspamGaps([id]).catch(() => {});
    setGaps(await adminApi.askCassie.gaps().catch(() => gaps));
  };

  const deleteSpam = async () => {
    if (!window.confirm(`Delete the ${gaps?.spam_count || 0} held back as spam?`)) return;
    const res = await adminApi.askCassie.deleteSpamGaps().catch(() => null);
    if (res) toast({ title: `Deleted ${res.deleted}` });
    setGaps(await adminApi.askCassie.gaps().catch(() => gaps));
  };

  const clearIgnored = async () => {
    const res = await adminApi.askCassie.clearIgnoredGaps().catch(() => null);
    if (res) toast({ title: `${res.deleted} ignored entr${res.deleted === 1 ? "y" : "ies"} removed` });
    setGaps(await adminApi.askCassie.gaps().catch(() => gaps));
  };

  const regenerate = async () => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await adminApi.askCassie.regenerate());
    } catch {
      toast({ title: "Couldn't rebuild the pages", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const approve = async (a) => {
    await adminApi.askCassie.approve(a.id, { publish: a.status !== "published" }).catch(() => {});
    await load();
  };

  const remove = async (a) => {
    await adminApi.askCassie.remove(a.id).catch(() => {});
    await load();
  };

  const saveSettings = async (patch) => {
    const cfg = await adminApi.askCassie.setConfig(patch).catch(() => null);
    if (!cfg) return toast({ title: "Couldn't save", variant: "destructive" });
    setOptions((o) => ({ ...o, config: cfg }));
    setReviewer(cfg.reviewer_stored || {});
    toast({ title: "Saved" });
  };

  const answers = data?.answers || [];
  const holding = answers.filter((a) => a.needs_subject);
  const filed = answers.filter((a) => !a.needs_subject);
  const cfg = options?.config || {};
  const live = !!cfg.hub_live;
  const libraryOnly = !!cfg.library_only_chat;
  const faqsRetired = !!cfg.faqs_retired;
  const live_reviewer = cfg.reviewer;
  const published = answers.filter((a) => a.status === "published").length;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return filed.filter((a) => {
      if (cat && a.category !== cat) return false;
      if (status && a.status !== status) return false;
      if (needle && !`${a.question} ${a.quick_answer}`.toLowerCase().includes(needle)) return false;
      if (review === "awaiting" && (a.practice || a.reviewed_at)) return false;
      if (review === "due" && !(a.review?.due || a.review?.overdue)) return false;
      if (review === "overdue" && !a.review?.overdue) return false;
      if (review === "paul" && a.practice) return false;
      return true;
    });
  }, [filed, q, cat, status, review]);

  const visible = filtered.slice(0, shown);
  const allSelected = visible.length > 0 && visible.every((a) => sel.includes(a.id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulk = async (action, extra = {}) => {
    if (!sel.length) return;
    setBusy(true);
    try {
      const res = await adminApi.askCassie.bulk({ ids: sel, action, ...extra });
      const skipped = res.skipped?.length || 0;
      toast({
        title: `${res.done} answer${res.done === 1 ? "" : "s"} ${
          { delete: "deleted", publish: "approved", unpublish: "back to draft", reviewed: "marked reviewed", reviewer: "assigned to Paul" }[action] || "moved"
        }`,
        description: skipped ? `${skipped} skipped — ${res.skipped[0].why}.` : undefined,
      });
      setSel([]);
      setMoveTo("");
      await load();
    } catch (e) {
      toast({ title: "Couldn't do that", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const assign = async (a, category) => {
    if (!category) return;
    await adminApi.askCassie.assignCategory(a.id, category).catch(() => toast({ title: "Couldn't move that one", variant: "destructive" }));
    await load();
  };

  const markReviewed = async (row) => {
    await adminApi.askCassie.approve(row.id, { publish: true }).catch(() => {});
    setReviews(await adminApi.askCassie.reviewReport().catch(() => reviews));
    await load();
  };

  return (
    <div data-testid="ask-cassie-manager">
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`ask-cassie-tab-${t.key}`}
            className={`text-sm font-bold px-4 py-2 rounded-full transition-colors ${tab === t.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-navy"}`}
          >
            {t.label}
            {t.key === "holding" && holding.length > 0 && (
              <span
                className={`ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded ${tab === t.key ? "bg-white/20" : "bg-orange/10 text-orange-dark"}`}
                data-testid="ask-cassie-holding-count"
              >
                {holding.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "answers" && (
        <>
          <Card
            title={`Answers (${filed.length})`}
            subtitle="Everything in the library. An answer stays a draft — reachable, but out of Google and with no reviewer line — until you approve it. Filter to a subject, work through the queue, or tick a batch and act on the lot."
            action={
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setQueue(filtered.filter((a) => a.status !== "published"))}
                  disabled={!filtered.some((a) => a.status !== "published")}
                  className="bg-teal hover:bg-teal/90 text-white rounded-full"
                  data-testid="ask-cassie-review-queue"
                >
                  <ListChecks size={15} className="mr-1.5" /> Review the drafts ({filtered.filter((a) => a.status !== "published").length})
                </Button>
                <Button onClick={() => setEditing({})} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="ask-cassie-new">
                  <Plus size={15} className="mr-1.5" /> New answer
                </Button>
              </div>
            }
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="relative">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className={`${input} pl-9`}
                  placeholder="Search the wording…"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setShown(PAGE); }}
                  data-testid="ask-cassie-filter-search"
                />
              </div>
              <select className={input} value={cat} onChange={(e) => { setCat(e.target.value); setShown(PAGE); }} data-testid="ask-cassie-filter-category">
                <option value="">Every subject</option>
                {(data?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
              <select className={input} value={status} onChange={(e) => { setStatus(e.target.value); setShown(PAGE); }} data-testid="ask-cassie-filter-status">
                <option value="draft">Drafts only</option>
                <option value="published">Approved only</option>
                <option value="">Drafts and approved</option>
              </select>
              <select className={input} value={review} onChange={(e) => { setReview(e.target.value); setShown(PAGE); }} data-testid="ask-cassie-filter-review">
                <option value="">Any review state</option>
                <option value="paul">Reviewed by Paul Barnes</option>
                <option value="awaiting">Awaiting a review</option>
                <option value="due">Due or overdue</option>
                <option value="overdue">Overdue only</option>
              </select>
            </div>

            {sel.length > 0 && (
              <div className="flex flex-wrap items-center gap-2.5 bg-navy/5 rounded-xl px-4 py-3 mb-4" data-testid="ask-cassie-bulk-bar">
                <span className="text-sm font-bold text-navy">{sel.length} selected</span>
                <Button onClick={() => bulk("publish")} disabled={busy} className="bg-teal hover:bg-teal/90 text-white rounded-full h-8 text-xs" data-testid="ask-cassie-bulk-publish">
                  Approve &amp; publish
                </Button>
                <Button onClick={() => bulk("unpublish")} disabled={busy} variant="outline" className="rounded-full h-8 text-xs" data-testid="ask-cassie-bulk-unpublish">
                  Back to draft
                </Button>
                <select
                  className="border border-slate-200 rounded-full px-3 h-8 text-xs text-navy bg-white"
                  value={moveTo}
                  onChange={(e) => { setMoveTo(e.target.value); if (e.target.value) bulk("category", { category: e.target.value }); }}
                  data-testid="ask-cassie-bulk-category"
                >
                  <option value="">Move to…</option>
                  {(data?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                  {data?.holding && <option value={data.holding.key}>{data.holding.name}</option>}
                </select>
                <Button onClick={() => bulk("reviewed")} disabled={busy} variant="outline" className="rounded-full h-8 text-xs" data-testid="ask-cassie-bulk-reviewed">
                  Mark reviewed today
                </Button>
                <Button onClick={() => bulk("reviewer")} disabled={busy} variant="outline" className="rounded-full h-8 text-xs" data-testid="ask-cassie-bulk-reviewer">
                  Assign Paul as reviewer
                </Button>
                <button onClick={() => bulk("delete")} disabled={busy} className="text-xs font-bold text-red-500 hover:text-red-700" data-testid="ask-cassie-bulk-delete">
                  Delete
                </button>
                <button onClick={() => setSel([])} className="text-xs text-slate-500 hover:text-navy ml-auto" data-testid="ask-cassie-bulk-clear">Clear</button>
              </div>
            )}

            {!data ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <>
                <p className="text-xs text-slate-400 mb-2" data-testid="ask-cassie-filter-count">
                  Showing {visible.length} of {filtered.length} matching · {published} approved in the library
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-3">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => setSel(allSelected ? [] : visible.map((a) => a.id))}
                          className="accent-orange w-4 h-4"
                          data-testid="ask-cassie-select-all"
                        />
                      </th>
                      <th className="py-2 pr-3">Question</th><th className="py-2 pr-3">Category</th>
                      <th className="py-2 pr-3">Kind</th><th className="py-2 pr-3">Google</th>
                      <th className="py-2 pr-3">Review</th><th className="py-2 pr-3">Status</th><th></th>
                    </tr></thead>
                    <tbody>
                      {visible.map((a) => (
                        <tr key={a.id} className="border-b border-slate-50" data-testid={`ask-cassie-row-${a.slug}`}>
                          <td className="py-2.5 pr-3">
                            <input
                              type="checkbox"
                              checked={sel.includes(a.id)}
                              onChange={() => toggle(a.id)}
                              className="accent-orange w-4 h-4"
                              data-testid={`ask-cassie-select-${a.slug}`}
                            />
                          </td>
                          <td className="py-2.5 pr-3 text-navy font-medium max-w-[340px]">
                            {a.level === "detailed" ? (
                              <a href={`/ask-cassie/${a.slug}`} target="_blank" rel="noreferrer" className="hover:text-orange">{a.question}</a>
                            ) : a.question}
                          </td>
                          <td className="py-2.5 pr-3 text-slate-500">{a.category_name}</td>
                          <td className="py-2.5 pr-3 text-slate-500">{a.level === "detailed" ? "Full page" : "Quick answer"}</td>
                          <td className="py-2.5 pr-3 text-xs" data-testid={`ask-cassie-index-${a.slug}`}>
                            {a.indexable ? <span className="text-slate-500">Indexed</span> : <span className="text-slate-400">Noindex</span>}
                          </td>
                          <td className="py-2.5 pr-3 text-slate-500">
                            {a.practice ? (
                              <span className="text-xs text-slate-400">Date only</span>
                            ) : (
                              <>
                                {a.review?.label}
                                {a.review?.overdue ? <span className="block text-[11px] font-bold text-orange-dark">Overdue</span>
                                  : a.review?.due ? <span className="block text-[11px] font-bold text-slate-400">Due</span> : null}
                              </>
                            )}
                          </td>
                          <td className="py-2.5 pr-3">
                            {a.status === "published" ? (
                              <span className="text-[11px] font-bold uppercase bg-teal/10 text-teal px-2 py-0.5 rounded">Published</span>
                            ) : (
                              <span className="text-[11px] font-bold uppercase bg-navy/5 text-navy px-2 py-0.5 rounded">Draft</span>
                            )}
                          </td>
                          <td className="py-2.5">
                            <div className="flex items-center gap-3 justify-end">
                              <button onClick={() => setEditing(a)} title="Edit" data-testid={`ask-cassie-edit-${a.slug}`} className="text-slate-400 hover:text-navy"><Pencil size={15} /></button>
                              <button
                                onClick={() => approve(a)}
                                data-testid={`ask-cassie-approve-${a.slug}`}
                                className="text-xs font-bold text-slate-500 hover:text-orange-dark whitespace-nowrap"
                              >
                                {a.status === "published" ? "Back to draft" : "Approve & publish"}
                              </button>
                              <button onClick={() => remove(a)} title="Delete" data-testid={`ask-cassie-delete-${a.slug}`} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filtered.length > visible.length && (
                  <button
                    onClick={() => setShown(shown + PAGE)}
                    className="mt-4 text-sm font-bold text-orange hover:text-orange-dark"
                    data-testid="ask-cassie-show-more"
                  >
                    Show {Math.min(PAGE, filtered.length - visible.length)} more
                  </button>
                )}
              </>
            )}
          </Card>

          <Card
            title="Crawler-readable pages"
            subtitle="Every Ask Cassie page is written to disk as real HTML — question, answer, headings, breadcrumbs, related links, title, description, canonical and structured data — so it reads without JavaScript. Pages are rebuilt on every deploy; this rebuilds them now."
          >
            <Button onClick={regenerate} disabled={busy} className="bg-navy hover:bg-navy/90 text-white rounded-full" data-testid="ask-cassie-regenerate">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <RefreshCw size={15} className="mr-1.5" />}
              Rebuild the pages now
            </Button>
            {result && (
              <div
                className={`mt-4 rounded-xl p-4 flex items-start gap-2.5 ${result.mode === "live" ? "bg-teal/5 border border-teal/30" : "bg-orange/5 border border-orange/30"}`}
                data-testid="ask-cassie-regenerate-result"
              >
                {result.mode === "live" ? <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="text-orange-dark shrink-0 mt-0.5" />}
                <div>
                  <p className="text-sm text-navy font-semibold">{result.mode === "live" ? "Live now" : "Goes out with the next deploy"}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{result.message}</p>
                </div>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-4">
              <a href="/api/ask-cassie/prerender" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-navy">
                See exactly what gets written <ExternalLink size={11} />
              </a>
            </p>
          </Card>
        </>
      )}

      {tab === "holding" && (
        <Card
          title={`Needs a subject (${holding.length})`}
          subtitle="Questions brought across that matched no subject and aren't about the firm. Nothing here is published, indexed or publicly visible — and nothing has been guessed at on your behalf. Give each one a category, or delete it."
        >
          {!data ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : holding.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="ask-cassie-holding-empty">
              Nothing waiting — every question in the library has a category.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ask-cassie-holding-list">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Question</th><th className="py-2 pr-3">Came from</th>
                  <th className="py-2 pr-3">Why it's here</th><th className="py-2 pr-3">Give it a category</th><th></th>
                </tr></thead>
                <tbody>
                  {holding.map((a) => (
                    <tr key={a.id} className="border-b border-slate-50 align-top" data-testid={`ask-cassie-holding-${a.slug}`}>
                      <td className="py-3 pr-3 text-navy font-medium max-w-[320px]">
                        {a.question}
                        <span className="block text-xs text-slate-400 font-normal mt-1 line-clamp-2">{a.quick_answer}</span>
                      </td>
                      <td className="py-3 pr-3 text-slate-500 text-xs whitespace-nowrap">
                        {a.appears_on || "—"}
                        {a.old_category && <span className="block text-slate-400 mt-0.5">was: {a.old_category}</span>}
                      </td>
                      <td className="py-3 pr-3 text-slate-500 text-xs max-w-[260px]">{a.migration_notes || "No note recorded"}</td>
                      <td className="py-3 pr-3">
                        <select
                          defaultValue=""
                          onChange={(e) => assign(a, e.target.value)}
                          className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-navy focus:outline-none focus:border-orange"
                          data-testid={`ask-cassie-assign-${a.slug}`}
                        >
                          <option value="">Choose…</option>
                          {(data.categories || []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                        </select>
                      </td>
                      <td className="py-3 text-right">
                        <button onClick={() => remove(a)} title="Discard" data-testid={`ask-cassie-holding-delete-${a.slug}`} className="text-red-400 hover:text-red-600">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "reviews" && (
        <Card
          title="Answers due a review"
          subtitle="A published answer carries Paul's name and a review date, so the date has to mean something. Tax, VAT, payroll and solicitors' answers come round every year; Xero every six months; evergreen ones every two years. Working with CASS answers aren't here — they show a last-updated date, not a reviewer. A reminder lands by email every month."
        >
          {!reviews ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : (reviews.overdue?.length || 0) + (reviews.due?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500" data-testid="ask-cassie-reviews-empty">
              Nothing due. All {reviews.published || 0} approved answers are within their cadence.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="ask-cassie-reviews">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3">Question</th><th className="py-2 pr-3">Subject</th>
                <th className="py-2 pr-3">Cadence</th><th className="py-2 pr-3">Last reviewed</th><th></th>
              </tr></thead>
              <tbody>
                {[...(reviews.overdue || []), ...(reviews.due || [])].map((r) => (
                  <tr key={r.id} className="border-b border-slate-50" data-testid={`ask-cassie-review-${r.slug}`}>
                    <td className="py-2.5 pr-3 text-navy font-medium max-w-[360px]">{r.question}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{r.category_name}</td>
                    <td className="py-2.5 pr-3 text-slate-500 text-xs">{r.frequency}</td>
                    <td className="py-2.5 pr-3 text-slate-500 text-xs">
                      {r.reviewed_at || "never"}
                      {(reviews.overdue || []).some((o) => o.id === r.id) && (
                        <span className="block text-[11px] font-bold text-orange-dark">Overdue</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(answers.find((a) => a.id === r.id) || null)} className="text-xs font-bold text-slate-500 hover:text-navy mr-3" data-testid={`ask-cassie-review-edit-${r.slug}`}>
                        Read it
                      </button>
                      <button onClick={() => markReviewed(r)} className="text-xs font-bold text-teal hover:text-teal/80" data-testid={`ask-cassie-review-done-${r.slug}`}>
                        Still right — mark reviewed
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "csv" && (
        <>
          <Card
            title="Moving the library to the live site"
            subtitle="This site and the live site keep their own databases. One file carries everything across — including the approvals and the settings a spreadsheet can't."
          >
            <AskCassieTransfer onRestored={load} />
          </Card>
          <Card
            title="Bulk editing in a spreadsheet"
            subtitle="Download the library, edit it in Excel or Google Sheets, and bring it back. Matching is by web address, so the same row updates the same answer. An import never publishes anything unless you say so, and it's all or nothing — if a row has a problem, nothing is written."
          >
            <AskCassieImport onImported={load} />
          </Card>
        </>
      )}

      {tab === "gaps" && (
        <Card
          title="What we can't answer yet"
          subtitle="Hub searches that found nothing and questions Cassie couldn't answer, grouped so twelve ways of asking the same thing are one thing to write. Where Search Console is connected, each gap is sized against what people are actually searching Google for — write those first. The wording only: no names, no emails, nothing that identifies anyone."
          action={
            gaps?.ignored_count > 0 && (
              <Button onClick={clearIgnored} variant="outline" className="rounded-full text-xs h-9" data-testid="gaps-clear-ignored">
                Clear the {gaps.ignored_count} ignored
              </Button>
            )
          }
        >
          {!gaps ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4" data-testid="gaps-summary">
                {gaps.theme_count} gap{gaps.theme_count === 1 ? "" : "s"} from {gaps.asked_count} question
                {gaps.asked_count === 1 ? "" : "s"}
                {gaps.search_console?.connected
                  ? ` · matched against ${gaps.search_console.queries} Search Console queries, last ${gaps.days} days`
                  : " · Search Console isn't connected, so there's no search demand to rank by"}
                {gaps.ignored_count > 0 && ` · ${gaps.ignored_count} ignored (lead details and test traffic, not questions)`}
                {gaps.answered_count > 0 && ` · ${gaps.answered_count} already marked as answered`}
                {gaps.spam_count > 0 && ` · ${gaps.spam_count} held back as spam`}
              </p>

              {gaps.spam_count > 0 && (
                <details className="mb-4 rounded-xl border border-slate-100 bg-slate-50/70 p-4" data-testid="gaps-spam-held">
                  <summary className="text-xs font-bold text-slate-500 cursor-pointer hover:text-navy">
                    {gaps.spam_count} caught as spam and kept off the list
                  </summary>
                  <ul className="mt-3 space-y-2">
                    {gaps.spam.map((s) => (
                      <li key={s.id} className="flex items-start gap-3 text-xs">
                        <span className="flex-1 text-slate-600 break-words">{s.text}</span>
                        <span className="text-slate-400 shrink-0">{s.reason}</span>
                        <button
                          onClick={() => unspamGap(s.id)}
                          className="font-semibold text-teal hover:underline shrink-0"
                          data-testid={`gap-unspam-${s.id}`}
                        >
                          Not spam
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Button onClick={deleteSpam} variant="outline" className="rounded-full text-xs h-9 mt-4 border-red-200 text-red-500 hover:bg-red-50" data-testid="gaps-delete-spam">
                    <Trash2 size={13} className="mr-1" /> Delete all of it
                  </Button>
                </details>
              )}

              {gaps.themes.length === 0 ? (
                <p className="text-sm text-slate-400">Nothing yet. This fills up as people use the hub and the chat.</p>
              ) : (
                <div className="space-y-3" data-testid="ask-cassie-gaps">
                  {gaps.themes.map((t, i) => (
                    <div key={i} className="rounded-xl border border-slate-100 p-4" data-testid={`gap-theme-${i}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-navy">{t.label}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Asked {t.count} time{t.count === 1 ? "" : "s"}
                            {t.asked.length > 1 && ` · ${t.asked.length} ways of asking it`}
                            {t.suggested_category_name && ` · looks like ${t.suggested_category_name}`}
                            {t.last_seen && ` · last ${String(t.last_seen).slice(0, 10)}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 shrink-0 ml-auto">
                          {t.search?.impressions > 0 && (
                            <div className="text-right" data-testid={`gap-search-${i}`}>
                              <p className="text-sm font-bold text-navy">{Math.round(t.search.impressions)}</p>
                              <p className="text-[11px] text-slate-400">
                                Google impressions{t.search.best_position ? ` · best position ${t.search.best_position}` : ""}
                              </p>
                            </div>
                          )}
                          <Button
                            onClick={() => setEditing({ preset: { question: t.label, category: t.suggested_category || (data?.categories || [])[0]?.key } })}
                            className="bg-orange hover:bg-orange-dark text-white rounded-full h-9 text-xs"
                            data-testid={`gap-draft-${i}`}
                          >
                            <Plus size={14} className="mr-1" /> Draft an answer
                          </Button>
                          <Button
                            onClick={() => resolveGap(t, "answered")}
                            variant="outline"
                            className="rounded-full h-9 text-xs border-teal/40 text-teal hover:bg-teal/10"
                            data-testid={`gap-answered-${i}`}
                          >
                            <Check size={14} className="mr-1" /> Mark answered
                          </Button>
                          <Button
                            onClick={() => resolveGap(t, "spam")}
                            variant="outline"
                            className="rounded-full h-9 text-xs border-red-200 text-red-500 hover:bg-red-50"
                            data-testid={`gap-delete-${i}`}
                          >
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        </div>
                      </div>

                      {(t.asked.length > 1 || (t.search?.queries || []).length > 0) && (
                        <details className="mt-3">
                          <summary className="text-xs font-bold text-slate-500 cursor-pointer hover:text-navy">Show the wording</summary>
                          <div className="mt-2 grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Asked here</p>
                              <ul className="mt-1.5 space-y-1">
                                {t.asked.map((a) => (
                                  <li key={a.id} className="text-xs text-slate-600 flex items-start gap-2">
                                    <span className="text-slate-400">{a.source === "cassie" ? <MessageCircle size={11} className="mt-0.5" /> : "🔍"}</span>
                                    <span className="flex-1">{a.text}</span>
                                    <button
                                      onClick={() => removeGap(a.id)}
                                      className="text-red-400 hover:text-red-600 shrink-0"
                                      data-testid={`gap-remove-${a.id}`}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {(t.search?.queries || []).length > 0 && (
                              <div>
                                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Searched on Google</p>
                                <ul className="mt-1.5 space-y-1">
                                  {t.search.queries.map((q) => (
                                    <li key={q.query} className="text-xs text-slate-600">
                                      {q.query} — <span className="text-slate-400">{Math.round(q.impressions)} impressions, position {q.position}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {tab === "audit" && <AskCassieAudit />}

      {tab === "stats" && <AskCassieStats />}
      {tab === "batch" && <AskCassieNewAnswers />}

      {tab === "video" && (
        options?.config
          ? <AskCassieVideoIntro config={options.config} onSaved={(c) => setOptions((o) => ({ ...o, config: c }))} />
          : <p className="text-sm text-slate-400">Loading…</p>
      )}

      {tab === "settings" && (
        <>
          <Card
            title="The reviewer profile"
            subtitle="The reviewer line on every published answer links here. It uses the Paul profile you already maintain on the site — the home page founder block first, the team page as a fallback — so you only write it once. Anything you type here overrides the inherited version for Ask Cassie only."
          >
            {!reviewer ? <p className="text-sm text-slate-400">Loading…</p> : (
              <div className="max-w-2xl">
                <div className="rounded-xl bg-slate-50 p-4 mb-5" data-testid="reviewer-inherited">
                  <div className="flex items-start gap-4">
                    {live_reviewer?.photo && (
                      <img src={live_reviewer.photo} alt={live_reviewer.name} className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
                    )}
                    <div>
                      <p className="text-sm font-bold text-navy">{live_reviewer?.name} — {live_reviewer?.role}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {live_reviewer?.source === "written for Ask Cassie"
                          ? "Written here, for Ask Cassie."
                          : live_reviewer?.source
                            ? `Pulled from ${live_reviewer.source}. Edit it there and this follows.`
                            : "Nothing to pull yet — no biography has been written on the home page or the team page."}
                        {live_reviewer?.photo ? " Photo from the team profile." : ""}
                      </p>
                      {live_reviewer?.bio && (
                        <p className="text-xs text-slate-600 mt-2 leading-relaxed line-clamp-3">{live_reviewer.bio}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={input} value={reviewer.name || ""} onChange={(e) => setReviewer({ ...reviewer, name: e.target.value })} placeholder="Name" data-testid="reviewer-name-input" />
                  <input className={input} value={reviewer.role || ""} onChange={(e) => setReviewer({ ...reviewer, role: e.target.value })} placeholder={live_reviewer?.role || "Role"} data-testid="reviewer-role-input" />
                </div>
                <textarea
                  rows={3}
                  className={`${input} mt-3`}
                  placeholder="What the reviewer badge covers — the line readers see under his name"
                  value={reviewer.remit || ""}
                  onChange={(e) => setReviewer({ ...reviewer, remit: e.target.value })}
                  data-testid="reviewer-remit-input"
                />
                <textarea
                  rows={5}
                  className={`${input} mt-3`}
                  placeholder={live_reviewer?.bio ? "Leave blank to use the biography from the site" : "A short biography — why this person is worth trusting on these subjects. Leave a blank line between paragraphs."}
                  value={reviewer.bio || ""}
                  onChange={(e) => setReviewer({ ...reviewer, bio: e.target.value })}
                  data-testid="reviewer-bio-input"
                />
                <textarea
                  rows={3}
                  className={`${input} mt-3`}
                  placeholder={(live_reviewer?.credentials || []).join("\n") || "Credentials — one per line"}
                  value={(reviewer.credentials || []).join("\n")}
                  onChange={(e) => setReviewer({ ...reviewer, credentials: e.target.value.split("\n") })}
                  data-testid="reviewer-credentials-input"
                />
                <input className={`${input} mt-3`} placeholder="LinkedIn URL (optional)" value={reviewer.linkedin || ""} onChange={(e) => setReviewer({ ...reviewer, linkedin: e.target.value })} />
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <Button onClick={() => saveSettings({ reviewer })} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="reviewer-save">Save profile</Button>
                  <a href={live_reviewer?.url} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-navy inline-flex items-center gap-1">
                    View the page <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            )}
          </Card>

          <Card
            title="Cassie answers from the library"
            subtitle="Cassie is given the approved answers relevant to whatever a visitor asks, and told to answer in that wording — so the chat and the site can't drift apart. Only approved answers count: a draft is copy nobody has signed off. The link back to the answer appears under her reply."
          >
            <p className="text-sm text-slate-600" data-testid="ask-cassie-grounding-state">
              {published === 0 ? (
                <>Nothing is grounding her yet — no answers are approved. Approve the ones you're happy with and she'll start using them immediately.</>
              ) : (
                <><strong className="text-navy">{published}</strong> approved answer{published === 1 ? " is" : "s are"} grounding Cassie right now.</>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-5">
              <Button
                onClick={() => saveSettings({ library_only_chat: !libraryOnly })}
                disabled={published === 0 && !libraryOnly}
                className={`rounded-full text-white ${libraryOnly ? "bg-navy hover:bg-navy/90" : "bg-teal hover:bg-teal/90"}`}
                data-testid="ask-cassie-library-only-toggle"
              >
                {libraryOnly ? "Let her use the old FAQ list again" : "Stop feeding her the old FAQ list"}
              </Button>
              <span className="text-sm text-slate-500 max-w-xl" data-testid="ask-cassie-library-only-state">
                {libraryOnly
                  ? "The old curated FAQ list is no longer given to her at all — the library is her only written source."
                  : "She still has the old curated FAQ list as a fallback, ranked below the library. Turn that off once the library covers the same ground — two lists is how the chat and the site end up saying different things."}
              </span>
            </div>
          </Card>

          <Card
            title="Launch the library"
            subtitle="While this is off, every Ask Cassie page is readable but carries noindex and stays out of the sitemap — so the pilot can be built and reviewed in the open without entering Google early. Turn it on when you're happy with the answers you've approved."
          >
            <div className="flex flex-wrap items-center gap-4">
              <Button
                onClick={() => saveSettings({ hub_live: !live })}
                className={`rounded-full text-white ${live ? "bg-navy hover:bg-navy/90" : "bg-teal hover:bg-teal/90"}`}
                data-testid="ask-cassie-launch-toggle"
              >
                {live ? "Take the library back out of Google" : "Launch — allow Google to index it"}
              </Button>
              <span className="text-sm text-slate-500" data-testid="ask-cassie-launch-state">
                {live
                  ? `Live: approved answers are indexable and in the sitemap (${published} approved).`
                  : "Not launched: everything is noindex, whatever its status."}
              </span>
            </div>
          </Card>

          <Card
            title="Retire the old FAQ page"
            subtitle="The old /faqs page and the library answer the same questions, which is two versions of the same answer. Retiring it sends /faqs to the library, takes it out of the sitemap, and points the navigation at Ask Cassie instead. Do it once the library covers the ground — the old page's questions are all in here as drafts."
          >
            <div className="flex flex-wrap items-center gap-4">
              <Button
                onClick={() => saveSettings({ faqs_retired: !faqsRetired })}
                disabled={!faqsRetired && (!live || published < 20)}
                className={`rounded-full text-white ${faqsRetired ? "bg-navy hover:bg-navy/90" : "bg-teal hover:bg-teal/90"}`}
                data-testid="ask-cassie-retire-faqs-toggle"
              >
                {faqsRetired ? "Put the FAQ page back" : "Retire /faqs — send it to the library"}
              </Button>
              <span className="text-sm text-slate-500 max-w-xl" data-testid="ask-cassie-retire-faqs-state">
                {faqsRetired
                  ? "Retired: /faqs redirects to the library, it's out of the sitemap, and the menus say Ask Cassie."
                  : !live
                    ? `Available once the library is launched and at least 20 answers are approved (${published} approved so far). Sending people from a live page to a noindex one would be worse than leaving it.`
                    : published < 20
                      ? `Available once at least 20 answers are approved (${published} so far). Retiring the old page while the library is thin loses answers people rely on.`
                      : "The FAQ page is still live alongside the library."}
              </span>
            </div>
          </Card>
        </>
      )}

      {editing && options && (
        <AskCassieEditor
          answer={editing.id ? editing : null}
          preset={editing.preset}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}

      {queue && options && queue.length > 0 && (
        <AskCassieReviewQueue
          queue={queue}
          options={options}
          onClose={() => setQueue(null)}
          onFinished={async (summary) => {
            toast({ title: `${summary.approved} approved`, description: `${summary.parked} parked, ${summary.deleted} deleted.` });
            await load();
          }}
        />
      )}
    </div>
  );
}
